import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import WPDropdown from "../../../packages/WPDropdown";

import DupontButton from "../../../components/DupontButton";
import DupontDialog from "../../../components/DupontDialog";
import DupontRadio from "../../../components/DupontRadio";
import InputBox from "../../../components/InputBox";
import { TextFieldValidator } from "../../../components/InputBox/TextFieldValidator";
import { SETTING_OPTIONS } from "./settingsOptions.config";

import {
  DEFAULT_CURRENCY_CODE,
  EXCHANGE_RATE_LABELS,
} from "../../../constants/currencies.constants";

import { NewBadgeIcon } from "../../../assets/icons";

import IconWithTooltip from "../../../common/styles/components/headings/IconWithTooltip";

import { formatUnitForDisplay } from "../../../utils/Utils";

import { storeUnits } from "../../../Store/TemplateStore/template.action";

import "./index.scss";

const Settings = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { disabled, setMandatoryComponentsStatus } = props;

  const {
    flow,
    pressure,
    feed_quality,
    permeate_quality,
    temperature,
    volume,
    currency,
    mass,
    conversion_rate,
    appLanguage,
    feed_water_factor,
    feed_water_permeate_factor,
  } = useSelector((state) => state);

  const [defaultSettingState, setDefaultSettingState] = useState({
    flow,
    pressure,
    feed_quality,
    permeate_quality,
    temperature,
    volume,
    currency,
    mass,
    conversion_rate,
    feed_water_factor,
    feed_water_permeate_factor,
  });
  const [openCurrencyConvertor, setOpenCurrencyConvertor] = useState(false);
  const [user_enter_conversion_rate, set_User_enter_conversion_rate] = useState(
    defaultSettingState.conversion_rate,
  );
  const [currencyRateError, setCurrencyRateError] = useState(false);
  const [openFeedQuality, setOpenFeedQuality] = useState(false);
  const [user_feed_conversion_factor, set_User_feed_conversion_factor] =
    useState(defaultSettingState.feed_water_factor);
  const [feedConversionError, setFeedConversionError] = useState(false);
  const [openPermeateQuality, setOpenPermeateQuality] = useState(false);
  const [user_permeate_conversion_factor, set_User_permeate_conversion_factor] =
    useState(defaultSettingState.feed_water_permeate_factor);
  const [permeateConversionError, setPermeateConversionError] = useState(false);
  const settingOptions = SETTING_OPTIONS();
  const currencyDisplay = EXCHANGE_RATE_LABELS[defaultSettingState.currency];

  const dispatch = useDispatch();

  useEffect(() => {
    setDefaultSettingState({
      flow,
      pressure,
      feed_quality,
      permeate_quality,
      temperature,
      volume,
      currency,
      mass,
      conversion_rate,
      appLanguage,
      feed_water_factor,
      feed_water_permeate_factor,
    });
    set_User_enter_conversion_rate(conversion_rate);
    set_User_feed_conversion_factor(feed_water_factor);
    set_User_permeate_conversion_factor(feed_water_permeate_factor);
  }, [
    flow,
    pressure,
    feed_quality,
    permeate_quality,
    temperature,
    volume,
    currency,
    mass,
    conversion_rate,
    appLanguage,
    feed_water_factor,
    feed_water_permeate_factor,
  ]);

  const settingsInformation = () => {
    const settingInformation = {
      flow: defaultSettingState.flow,
      pressure: defaultSettingState.pressure,
      feed_quality: defaultSettingState.feed_quality,
      permeate_quality: defaultSettingState.permeate_quality,
      temperature: defaultSettingState.temperature,
      volume: defaultSettingState.volume,
      currency: defaultSettingState.currency,
      mass: defaultSettingState.mass,
      conversion_rate:
        defaultSettingState.currency === DEFAULT_CURRENCY_CODE
          ? 1
          : defaultSettingState.conversion_rate,
      feed_water_factor: feed_water_factor,
      feed_water_permeate_factor: feed_water_permeate_factor,
    };
    dispatch(storeUnits(settingInformation));
  };

  React.useImperativeHandle(ref, () => ({ settingsInformation }));

  const handleOptionChange = (event) => {
    const { name, value } = event.target;
    const updatedSettings = {
      ...defaultSettingState,
      [name]: value,
    };

    setDefaultSettingState(updatedSettings);
    dispatch(storeUnits(updatedSettings));

    // Update mandatory component status - settings always fulfilled (all options valid)
    if (setMandatoryComponentsStatus) {
      setMandatoryComponentsStatus((prev) => ({
        ...prev,
        settings: { ...prev.settings, changed: true, fulfilled: true },
      }));
    }
  };

  const onProceedClicked = async (value) => {
    setOpenCurrencyConvertor(false);
    setCurrencyRateError("");

    if (value === "CONFIRM") {
      dispatch(
        storeUnits({
          ...defaultSettingState,
          conversion_rate: user_enter_conversion_rate,
        }),
      );
      setDefaultSettingState({
        ...defaultSettingState,
        conversion_rate: user_enter_conversion_rate,
      });
    } else {
      set_User_enter_conversion_rate(defaultSettingState.conversion_rate);
    }
  };

  const onFactorProceedClicked = async (value) => {
    setOpenFeedQuality(false);
    setFeedConversionError("");

    if (value === "CONFIRM") {
      dispatch(
        storeUnits({
          ...defaultSettingState,
          feed_water_factor: user_feed_conversion_factor,
        }),
      );
      setDefaultSettingState({
        ...defaultSettingState,
        feed_water_factor: user_feed_conversion_factor,
      });
    } else {
      set_User_feed_conversion_factor(defaultSettingState.feed_water_factor);
    }
  };

  const onPermeateFactorProceedClicked = async (value) => {
    setOpenPermeateQuality(false);
    setPermeateConversionError("");

    if (value === "CONFIRM") {
      dispatch(
        storeUnits({
          ...defaultSettingState,
          feed_water_permeate_factor: user_permeate_conversion_factor,
        }),
      );
      setDefaultSettingState({
        ...defaultSettingState,
        feed_water_permeate_factor: user_permeate_conversion_factor,
      });
    } else {
      set_User_permeate_conversion_factor(
        defaultSettingState.feed_water_permeate_factor,
      );
    }
  };

  const handleCurrencyChange = (option) => {
    const conversionRate =
      option.conversionRatio ?? defaultSettingState.conversion_rate;
    set_User_enter_conversion_rate(conversionRate);
    const updated = {
      ...defaultSettingState,
      currency: option.value,
      conversion_rate: conversionRate,
    };
    setDefaultSettingState(updated);
    dispatch(storeUnits(updated));
  };

  // Custom currency options filter
  const filterCurrencyOption = ({ data }, inputValue) => {
    const normalizedInput = (inputValue || "").trim().toLowerCase();

    if (!normalizedInput) {
      return true;
    }

    const searchableText = data?.searchText.toLowerCase();

    return searchableText.includes(normalizedInput);
  };

  const handleConversionRateChange = (value) => {
    setCurrencyRateError("");
    if (value <= 0) {
      setCurrencyRateError(t("settingStrings.rateError"));
    }
    set_User_enter_conversion_rate(value);
  };
  const handleFeedRateChange = (value) => {
    setFeedConversionError("");
    if (value <= 0) {
      setFeedConversionError(t("settingStrings.conversionRateError"));
    }
    set_User_feed_conversion_factor(value);
  };
  const handlePermeateRateChange = (value) => {
    setPermeateConversionError("");
    if (value <= 0) {
      setPermeateConversionError(t("settingStrings.conversionRateError"));
    }
    set_User_permeate_conversion_factor(value);
  };

  const getOptionDisplayText = (settingKey, optionValue) => {
    const keysToFormat = ["feed_quality", "permeate_quality", "temperature"];
    return keysToFormat.includes(settingKey)
      ? formatUnitForDisplay(optionValue)
      : optionValue;
  };

  const handleOpenCurrencyConverter = () => {
    set_User_enter_conversion_rate(defaultSettingState.conversion_rate);
    setOpenCurrencyConvertor(true);
  };

  return (
    <div className="ro-card-wrapper position-relative">
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading">{t("settingStrings.settings")}</div>
          {!disabled && (
            <IconWithTooltip label={t("helpInformation.settingsHelp")} />
          )}
        </div>
      </div>
      <div className="grid-container-settings">
        {Object.keys(settingOptions).map((key) => (
          <div className="grid-item-settings" key={key}>
            <div className="checkbox-label">
              {settingOptions[key].showNewBadge ? (
                <span className="new-badge-inline-wrapper">
                  <span>{t(`settingStrings.${key}`)}</span>
                  <NewBadgeIcon className="new-badge" />
                </span>
              ) : (
                t(`settingStrings.${key}`)
              )}
            </div>
            <div onChange={handleOptionChange}>
              {key !== "currency" &&
                Object.values(settingOptions[key].options).map((option) => (
                  <DupontRadio
                    key={option}
                    text={getOptionDisplayText(key, option)}
                    checked={defaultSettingState[key] === option}
                    value={option}
                    name={key}
                    disabled={disabled ? option : disabled}
                  />
                ))}

              {key === "currency" && (
                <div className="currency-dropdown-wrapper">
                  <WPDropdown
                    id={"currency_dropdown"}
                    value={defaultSettingState[key]}
                    options={settingOptions[key].options}
                    onChange={handleCurrencyChange}
                    filterOption={filterCurrencyOption}
                    disabled={disabled}
                    customClassName="currency-dropdown"
                  />
                  <DupontButton
                    key="currencyButton"
                    type="tertiary"
                    customClass="conversion-link-button"
                    onClick={handleOpenCurrencyConverter}
                    disabled={
                      disabled ||
                      defaultSettingState["currency"] === DEFAULT_CURRENCY_CODE
                    }
                  >
                    {t("settingStrings.exchangeRate")}
                  </DupontButton>
                </div>
              )}

              {key === "feed_quality" && (
                <DupontButton
                  key={key}
                  type="tertiary"
                  customClass="conversion-link-button"
                  onClick={() => setOpenFeedQuality(!openFeedQuality)}
                  disabled={disabled ? key : disabled}
                >
                  {t("settingStrings.conductivityConversionFactor")}
                </DupontButton>
              )}
              {key === "permeate_quality" && (
                <DupontButton
                  key={key}
                  type="tertiary"
                  customClass="conversion-link-button"
                  onClick={() => setOpenPermeateQuality(!openPermeateQuality)}
                  disabled={disabled ? key : disabled}
                >
                  {t("settingStrings.conductivityConversionFactor")}
                </DupontButton>
              )}
            </div>
          </div>
        ))}

        {openCurrencyConvertor && (
          <DupontDialog
            open={openCurrencyConvertor}
            title={t("settingStrings.enterRate")}
            cancelBtnText={t("ValueStrings.cancel")}
            confirmBtnText={t("ValueStrings.confirm")}
            onClose={onProceedClicked}
            confirmDisabled={!user_enter_conversion_rate || currencyRateError}
          >
            <div>
              <div className="flex-ai-center ">
                <InputBox
                  id="currency-conversion-rate"
                  value={user_enter_conversion_rate}
                  placeholder={t("settingStrings.exchangeRate")}
                  maxLength={10}
                  onChange={handleConversionRateChange}
                  customClass={"text-left width100"}
                  validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
                  errorMsg={currencyRateError}
                />
                <span className="bodyMainHeadingText">{currencyDisplay}</span>
              </div>

              <div className="descriptionText text-left margin-top-10">
                {t("settingStrings.rateNote")}
              </div>
            </div>
          </DupontDialog>
        )}
        {openFeedQuality && (
          <DupontDialog
            open={openFeedQuality}
            title={t("settingStrings.enterConversionRate")}
            cancelBtnText={t("ValueStrings.cancel")}
            confirmBtnText={t("ValueStrings.confirm")}
            onClose={onFactorProceedClicked}
            confirmDisabled={
              !user_feed_conversion_factor || feedConversionError
            }
          >
            <div>
              <div className="flex-ai-center ">
                <InputBox
                  id="feed-conversion-factor"
                  value={user_feed_conversion_factor}
                  placeholder={t("settingStrings.enterConversionRate")}
                  maxLength={10}
                  onChange={handleFeedRateChange}
                  customClass={"text-left width100"}
                  validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
                  errorMsg={feedConversionError}
                />
              </div>
            </div>
          </DupontDialog>
        )}
        {openPermeateQuality && (
          <DupontDialog
            open={openPermeateQuality}
            title={t("settingStrings.enterConversionRate")}
            cancelBtnText={t("ValueStrings.cancel")}
            confirmBtnText={t("ValueStrings.confirm")}
            onClose={onPermeateFactorProceedClicked}
            confirmDisabled={
              !user_permeate_conversion_factor || permeateConversionError
            }
          >
            <div>
              <div className="flex-ai-center ">
                <InputBox
                  id="permeate-conversion-factor"
                  value={user_permeate_conversion_factor}
                  placeholder={t("settingStrings.enterConversionRate")}
                  maxLength={10}
                  onChange={handlePermeateRateChange}
                  customClass={"text-left width100"}
                  validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
                  errorMsg={permeateConversionError}
                />
              </div>
            </div>
          </DupontDialog>
        )}
      </div>
    </div>
  );
});

export default Settings;
